<template>
  <div>
    <h3>
      These tools will help you to monitor and manage conversions, choose the
      best creatives and traffic for it.
    </h3>
    <el-container class="tracker">
      <el-row :gutter="10">
        <el-col :xs="24" :sm="24" :md="24" :lg="24" :xl="24">
          <el-card class="box-card">
            <p>
              Postback URL is a multipurpose way to track conversion. It can be
              used both with a client-side and server-side logic. It may track
              conversions even in case if the client has cleaned a cookie or
              changed browser.
            </p>
            <el-table :data="tableData" style="width: 100%">
              <el-table-column width="40">
                <template #default="scope">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="scope.row.tooltip"
                    placement="top"
                  >
                    <el-icon>
                      <QuestionFilledElementIcon />
                    </el-icon>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column
                prop="name"
                label="Name"
                :width="isDesktop ? 140 : 100"
              />
              <el-table-column
                prop="param"
                label="3rd party parameter"
                :width="isDesktop ? 180 : 100"
              >
                <template #default="scope">
                  <el-input
                    v-model="scope.row.param"
                    placeholder="Parameter"
                    @update:modelValue="buildUrl"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="macros" label="Traforama macros" />
            </el-table>
            <p>Your post back url will be like this:</p>
            <el-input v-model="cpaLink" type="textarea" />
            <p>Please append click ID to creative URLs &{{ id }}=%ID%</p>
          </el-card>
        </el-col>
      </el-row>
    </el-container>
    <h3>Easy set up with the most popular trackers.</h3>
    <el-container class="tracker">
      <el-row :gutter="10">
        <el-col :xs="24" :sm="24" :md="24" :lg="24" :xl="24">
          <el-card class="box-card">
            <el-row
              v-for="(rowItems, index) in chunk(trackers, itemsInRow[colClass])"
              :key="index"
            >
              <el-col
                v-for="item in rowItems"
                :key="item.title"
                :xs="24"
                :sm="24"
                :md="12"
                :lg="8"
                :xl="6"
              >
                <div class="item-box">
                  <a
                    :href="`https://support.traforama.com/en/articles/${item.path}`"
                    target="_blank"
                  >
                    <img :src="item.image" :title="item.title" />
                    <br />
                    <strong>{{ item.title }}</strong>
                  </a>
                </div>
              </el-col>
            </el-row>
          </el-card>
        </el-col>
      </el-row>
    </el-container>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive } from 'vue'
import { useUserStore } from '@/store/modules/user'
import useResize from '@/hooks/useResize'
import chunk from '@/utils/array'
import logos from '@/apps/traforama/assets/trackers'

import { PA4KA_DOMAIN } from '@/apps/traforama/constants'

interface ITableRow {
  name: string
  macros: string
  param: string
  default: string
  tooltip: string
}

export default defineComponent({
  name: 'ConversionTracker',
  setup(_props, context) {
    const userStore = useUserStore()
    const postbackValue = `https://${PA4KA_DOMAIN}/api/click?suid=${userStore.suid}&c={c}&cpa={cpa}&id={ID}`
    const cpaLink = ref(postbackValue.replace('{c}', '1'))
    const id = ref('id')
    const { isDesktop, colClass } = useResize()
    const itemsInRow = { xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }

    const trackers = [
      {
        title: 'AdsBridge',
        image: logos.adsbridge,
        path: 'adsbridge'
      },
      {
        title: 'Bemob',
        image: logos.bemob,
        path: 'bemob'
      },
      {
        title: 'Binom',
        image: logos.binom,
        path: 'binom'
      },
      {
        title: 'FunnelFlux',
        image: logos.funnelflux,
        path: 'funnelflux'
      },
      {
        title: 'Keitaro',
        image: logos.keitaro,
        path: 'keitaro'
      },
      {
        title: 'Peerclick',
        image: logos.peerclick,
        path: 'peerclick'
      },
      {
        title: 'Voluum',
        image: logos.voluum,
        path: 'voluum'
      },
      {
        title: 'CPV ONE',
        image: logos.cpvone,
        path: 'cpv-one'
      }
    ]

    const tableData = reactive<Array<ITableRow>>([
      {
        name: 'Click ID',
        macros: 'ID',
        param: '',
        default: '{ID}',
        tooltip:
          'Traforama Click ID of the visitor session. This parameter is mandatory if you want to track conversions to Traforama by using S2S conversion tracker. Example: {clickid}, {click_id}, {{id}}.'
      },
      {
        name: 'Price',
        macros: 'cpa',
        param: '',
        default: '{cpa}',
        tooltip:
          'This parameter is optional. You may pass the price of each conversion using parameters. Example: {price}, {payout}, {{MONEY}}.'
      },
      {
        name: 'Event type',
        macros: 'c',
        param: '1',
        default: '1',
        tooltip:
          'This parameter is optional. Use c= from 1 to 10 for different types of conversions. For example, c=1 - visit to a landing page, c=2 - click on ‘buy’ button, c=3 - payment done.'
      }
    ])

    const buildUrl = () => {
      let link = postbackValue
      tableData.forEach((obj: ITableRow) => {
        link = link.replace(`{${obj.macros}}`, obj.param || obj.default)
        if (obj.macros === 'ID') {
          id.value = obj.param.replace(/{|}/g, '') || 'id'
        }
      })
      cpaLink.value = link
    }

    return {
      cpaLink,
      tableData,
      buildUrl,
      id,
      trackers,
      isDesktop,
      chunk,
      itemsInRow,
      colClass
    }
  }
})
</script>
<style type="text/css">
.tracker .el-card__header {
  padding: 0 18px;
}
.tracker .el-row {
  width: 100%;
}
.item-box {
  text-align: center;
  margin: 10px;
}
.item-box img {
  max-width: 300px;
}
.item-box a {
  text-decoration: underline;
}
</style>
